import React, { useState } from 'react';
import { FaCheck, FaHome } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';

// Import step components
import LocationStep from './steps/LocationStep';
import RoomSizeStep from './steps/RoomSizeStep';
import IntendedUseStep from './steps/IntendedUseStep';
import HireDurationStep from './steps/HireDurationStep';
import ContactInfoStep from './steps/ContactInfoStep';
import PreviewSubmitStep from './steps/PreviewSubmitStep';
import DataSummary from '../DataSummary';

const steps = [
  { 
    title: "Location",
    component: LocationStep,
    fields: ['deliveryAddress', 'specialConditions', 'siteType', 'powerSupply']
  },
  { 
    title: "Room Size & Capacity",
    component: RoomSizeStep,
    fields: ['roomType', 'capacity', 'sizeComment']
  },
  { 
    title: "Intended Use",
    component: IntendedUseStep,
    fields: ['temperatureRange', 'powerSource', 'shelving', 'lighting', 'security', 'guestCount', 'useComment']
  },
  { 
    title: "Hire Duration",
    component: HireDurationStep,
    fields: ['rentalDuration', 'deliveryDate', 'collectionDate', 'durationComment']
  },
  { 
    title: "Contact Information",
    component: ContactInfoStep,
    fields: ['email', 'fullName', 'mobileNumber']
  },
  {
    title: "Preview & Submit",
    component: PreviewSubmitStep,
    fields: []
  }
];

const CoolroomWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    // Location
    deliveryAddress: '',
    specialConditions: '',
    siteType: '',
    powerSupply: '',
    // Room Size & Capacity
    roomType: '',
    capacity: '',
    sizeComment: '',
    // Intended Use
    temperatureRange: '',
    powerSource: '',
    shelving: false,
    lighting: false,
    security: false,
    guestCount: '',
    useComment: '',
    // Hire Duration
    rentalDuration: '',
    deliveryDate: '',
    collectionDate: '',
    durationComment: '',
    // Contact Info
    email: '',
    fullName: '',
    mobileNumber: '',
    companyName: '',
    position: '',
    alternativeContactName: '',
    alternativeContactMobile: '',
    alternativeContactEmail: '',
  });
  const [errors, setErrors] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { login, user } = useAuth();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prevErrors => ({ ...prevErrors, [name]: null }));
    }
  };

  const updateFormData = (newData) => {
    setFormData(prevData => ({
      ...prevData,
      ...newData
    }));
  };

  const validateStep = () => {
    const newErrors = {};
    const fieldsToValidate = steps[currentStep].fields;
  
    fieldsToValidate.forEach(field => {
      if (field === 'deliveryDate' || field === 'rentalDuration' || field === 'email' || field === 'fullName' || field === 'mobileNumber') {
        if (!formData[field]) {
          newErrors[field] = 'This field is required';
        }
      } else if (!formData[field] && field !== 'sizeComment' && field !== 'useComment' && field !== 'durationComment' && field !== 'deliveryInstructions') {
        newErrors[field] = 'This field is required';
      }
    });
  
    // Special validation for email field
    if (fieldsToValidate.includes('email') && formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
  
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prevStep => prevStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prevStep => prevStep - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateStep()) {
      console.log('Form submitted:', formData);
      // Here you would typically send the data to your backend
    }
  };

  const handleHomeNavigation = () => {
    setShowConfirmDialog(true);
  };

  const confirmHomeNavigation = () => {
    setShowConfirmDialog(false);
    navigate('/');
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-between mb-8">
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          <div className={`flex flex-col items-center ${index <= currentStep ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`rounded-full transition duration-500 ease-in-out h-12 w-12 py-3 border-2 flex items-center justify-center ${
              index < currentStep ? 'bg-blue-600 border-blue-600' : 
              index === currentStep ? 'border-blue-600' : 'border-gray-300'
            }`}>
              {index < currentStep ? (
                <FaCheck className="text-white" />
              ) : (
                <span className={index === currentStep ? 'text-blue-600' : 'text-gray-500'}>{index + 1}</span>
              )}
            </div>
            <p className="text-xs mt-2">{step.title}</p>
          </div>
          {index < steps.length - 1 && (
            <div className={`flex-auto border-t-2 transition duration-500 ease-in-out ${
              index < currentStep ? 'border-blue-600' : 'border-gray-300'
            }`}></div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const CurrentStepComponent = steps[currentStep].component;

  const commonProps = {
    formData,
    handleInputChange,
    updateFormData,
    errors,
    user,
    login,
  };

  const ConfirmationDialog = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Confirm Navigation</h2>
        <p className="mb-6">Navigating away from this page will result in losing all entered Coolroom Request data. Are you sure you want to proceed?</p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={() => setShowConfirmDialog(false)}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={confirmHomeNavigation}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto mt-10 p-6 bg-white rounded-lg shadow-xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-blue-600">Coolroom Requirements</h1>
        <button
          onClick={handleHomeNavigation}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors flex items-center"
        >
          <FaHome className="mr-2" /> Home
        </button>
      </div>
      {showConfirmDialog && <ConfirmationDialog />}
      <StepIndicator />
      <div className="flex flex-col md:flex-row md:space-x-6">
        <div className="flex-grow mb-6 md:mb-0">
          <form onSubmit={handleSubmit}>
            <CurrentStepComponent 
              {...commonProps}
            />
            <div className="mt-8 flex justify-between">
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition-colors"
                >
                  Previous
                </button>
              )}
              {currentStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
                >
                  Submit
                </button>
              )}
            </div>
          </form>
        </div>
        <div className="w-full md:w-1/3">
          <DataSummary formData={formData} steps={steps} />
        </div>
      </div>
    </div>
  );
};

export default CoolroomWizard;